'use client';

import { formatPhone } from '@/helpers/formatPhone';

import { ContactInfoStepActions, isValid } from './state';
import { RepairFormActionMap } from '../../reducer';

import { InputText } from '@/components/atoms/Inputs/InputText';
import { InputEmail } from '@/components/atoms/Inputs/InputEmail';
import { HeadingText } from '@/components/atoms/HeadingText';
import {
	FormContext_Actions,
	useFormContext,
} from '@/components/forms/FormContext';
import { Step } from '@/components/forms/Step';
import { useValidateStep } from '@/components/forms/useValidateStep';
import { InputPhone } from '@/components/atoms/Inputs/InputPhone';
import { SpaceWow } from '@/components/SpaceWow';
import { TextAlert } from '@/components/calculators/TextAlert';
import { IconInfoBubble } from '@/components/icons';
import { SelectReferralSource } from '@/components/SelectReferralSource';
import { ConfidentialityNotice } from '@/components/atoms/ConfidentialityNotice';

import { RepairFormState, StepMap, steps } from '../../constants';
import { useSplitAndHeap } from '@/hooks/useSplitAndHeap';
import { isPhoneRequiredFromSplit } from './helpers/isPhoneRequiredFromSplit';
import { getPhoneErrorText } from './helpers/getPhoneErrorText';
import { useCallback, useMemo } from 'react';

export interface ContactInfoStepProps {
	testId?: string;
}

const ContactInfoStep = (props: ContactInfoStepProps) => {
	const { testId = 'ContactInfoStep' } = props;

	const { state, dispatch } = useFormContext<
		StepMap,
		RepairFormState,
		RepairFormActionMap
	>();
	const { treatment: PhoneNumberRequirement, config: phoneRequirementConfig } =
		useSplitAndHeap('PhoneNumberRequirement').PhoneNumberRequirement;

	const phoneTreatmentObject = phoneRequirementConfig
		? JSON.parse(phoneRequirementConfig)
		: null;

	const showValidationErrors = state.showValidationErrors;

	const phoneRequiredFromSplit = useMemo(
		() =>
			isPhoneRequiredFromSplit(
				PhoneNumberRequirement,
				phoneTreatmentObject?.Repair,
			),
		[PhoneNumberRequirement, phoneTreatmentObject?.Repair],
	);

	const phoneErrorText = useMemo(
		() =>
			getPhoneErrorText(
				showValidationErrors,
				phoneRequiredFromSplit,
				state.phone,
			),
		[showValidationErrors, phoneRequiredFromSplit, state.phone],
	);

	const validateWithPhoneRequirement = useCallback(
		(state: RepairFormState) => isValid(state, phoneRequiredFromSplit),
		[phoneRequiredFromSplit],
	);

	useValidateStep<StepMap, RepairFormState, RepairFormActionMap>(
		validateWithPhoneRequirement,
		steps.ContactInfo,
	);

	function handleFirstNameChange(event: React.ChangeEvent<HTMLInputElement>) {
		dispatch({
			type: ContactInfoStepActions.UPDATE_FIRST_NAME,
			payload: event.target.value,
		});
	}

	function handleLastNameChange(event: React.ChangeEvent<HTMLInputElement>) {
		dispatch({
			type: ContactInfoStepActions.UPDATE_LAST_NAME,
			payload: event.target.value,
		});
	}

	function handleEmailChange(event: React.ChangeEvent<HTMLInputElement>) {
		dispatch({
			type: ContactInfoStepActions.UPDATE_EMAIL,
			payload: event.target.value,
		});
		dispatch({
			type: FormContext_Actions.SET_FIELD_ERRORS,
			payload: { ...state.fieldErrors, email: '' },
		});
		dispatch({
			type: FormContext_Actions.SHOW_VALIDATION_ERRORS,
			payload: false,
		});
	}

	function handlePhoneChange(event: React.ChangeEvent<HTMLInputElement>) {
		const formattedPhone = formatPhone(event.target.value);
		dispatch({
			type: ContactInfoStepActions.UPDATE_PHONE,
			payload: formattedPhone,
		});
	}

	function handleReferralSourceChange(
		event: React.ChangeEvent<HTMLSelectElement>,
	) {
		dispatch({
			type: ContactInfoStepActions.UPDATE_REFERRAL_SOURCE,
			payload: event.target.value,
		});
	}

	const getEmailError = () => {
		if (showValidationErrors) {
			if (!state.email) {
				return 'A valid email is required';
			} else if (state.fieldErrors.email) {
				return state.fieldErrors.email;
			}
		}
		return '';
	};

	return (
		<div data-testid={testId} className="mt-10 lg:mt-20">
			<Step<StepMap, RepairFormState, RepairFormActionMap>
				name={steps.ContactInfo}
			>
				<HeadingText tag="h1" size="h2">
					Who are we helping today?
				</HeadingText>

				<SpaceWow spacer_10 lg__spacer_14 />

				<div className="mx-auto mb-10 max-w-101">
					<InputText
						name="firstName"
						id="firstName"
						label="First name"
						fieldsetClassName="grid mb-4"
						onChange={handleFirstNameChange}
						value={state.firstName}
						required
						error={
							showValidationErrors && !state.firstName
								? 'First name is required'
								: ''
						}
					/>
					<InputText
						name="lastName"
						id="lastName"
						label="Last name"
						fieldsetClassName="grid mb-4"
						onChange={handleLastNameChange}
						value={state.lastName}
						required
						error={
							showValidationErrors && !state.lastName
								? 'Last name is required'
								: ''
						}
					/>
					<InputEmail
						name="email"
						id="email"
						label="Email address"
						fieldsetClassName="grid mb-4"
						onChange={handleEmailChange}
						value={state.email}
						required
						error={getEmailError()}
					/>
					<InputPhone
						name="phone"
						id="phone"
						label="Phone number"
						fieldsetClassName="grid mb-4"
						onChange={handlePhoneChange}
						value={state.phone}
						required={phoneRequiredFromSplit || state.phone.length !== 0}
						error={phoneErrorText}
					/>
					<SelectReferralSource
						label="How'd you hear about us? (optional)"
						value={state.referralSource}
						onChange={handleReferralSourceChange}
					/>
				</div>
				<SpaceWow spacer_08 lg__spacer_10 />
				<div className="mx-auto max-w-2xl">
					<TextAlert
						renderIcon={IconInfoBubble}
						text="This is the information your technician will use to contact you."
						title="Why are we asking?"
					/>
				</div>
				<SpaceWow spacer_08 lg__spacer_10 />
				<ConfidentialityNotice classNames="mb-20 mt-4 lg:mt-2" />
			</Step>
		</div>
	);
};

export default ContactInfoStep;
